<template>
  <div class="media-analysis-section">
    <div class="analysis-controls">
      <!-- 期間選択 -->
      <div class="period-selector">
        <select v-model="selectedPeriod" @change="onPeriodChange" class="period-select">
          <option v-for="option in periodOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        
        <!-- カスタム期間選択 -->
        <div v-if="selectedPeriod === 'custom'" class="custom-date-picker">
          <input 
            type="date" 
            v-model="customStartDate" 
            class="date-input"
            :max="customEndDate || today"
            @change="onPeriodChange"
          />
          <span>〜</span>
          <input 
            type="date" 
            v-model="customEndDate" 
            class="date-input"
            :min="customStartDate"
            :max="today"
            @change="onPeriodChange"
          />
        </div>
      </div>
      
      <!-- 更新ボタン -->
      <button @click="refreshData" :disabled="isLoading" class="refresh-btn">
        <i class="fas fa-sync-alt" :class="{'fa-spin': isLoading}"></i>
        更新
      </button>
    </div>
    
    <!-- タブナビゲーション -->
    <div class="tab-navigation">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        @click="activeTab = tab.id"
        :class="['tab-button', { active: activeTab === tab.id }]"
      >
        {{ tab.label }}
      </button>
    </div>
    
    <!-- コンテンツエリア -->
    <div class="content-area">
      <!-- ローディング表示 -->
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p>データを読み込んでいます...</p>
      </div>
      
      <!-- エラー表示 -->
      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-circle error-icon"></i>
        <p class="error-message">{{ error }}</p>
        <button @click="refreshData" class="retry-btn">再試行</button>
      </div>
      
      <!-- コンテンツ表示 -->
      <div v-else>
        <!-- サマリーページ -->
        <div v-if="activeTab === 'summary'" class="summary-content">
          <MediaAnalysisTable :data="mediaData" :is-loading="false" />
        </div>
        
        <!-- 媒体別ページ -->
        <div v-else class="media-detail-content">
          <MediaDetailView :media-type="activeTab" :data="getMediaData(activeTab)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import MediaAnalysisTable from '../MediaAnalysis/MediaAnalysisTable.vue'
import MediaDetailView from '../MediaAnalysis/MediaDetailView.vue'

export default {
  name: 'MediaAnalysisSection',
  components: {
    MediaAnalysisTable,
    MediaDetailView
  },
  props: {
    companyId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'summary',
      selectedPeriod: 'current_month',
      isLoading: false,
      mediaData: [],
      customStartDate: '',
      customEndDate: '',
      error: null,
      tabs: [{ id: 'summary', label: 'サマリー' }],
      periodOptions: [
        { value: 'current_month', label: '当月' },
        { value: 'last_month', label: '先月' },
        { value: 'last_3_months', label: '過去3ヶ月' },
        { value: 'last_6_months', label: '過去6ヶ月' },
        { value: 'last_year', label: '過去1年' },
        { value: 'custom', label: 'カスタム期間' }
      ]
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    today() {
      return new Date().toISOString().split('T')[0]
    }
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    ...mapActions('directtalk', ['fetchMediaAnalysis']),
    
    async refreshData() {
      this.isLoading = true
      this.error = null
      
      try {
        const params = this.getPeriodParams()
        const response = await this.fetchMediaAnalysis(params)
        if (response && response.media_list) {
          this.mediaData = response.media_list
          this.generateTabs()
        }
      } catch (error) {
        console.error('データ取得エラー:', error)
        this.error = 'データの取得に失敗しました'
      } finally {
        this.isLoading = false
      }
    },
    
    getPeriodParams() {
      const today = new Date()
      let start_date, end_date
      
      if (this.selectedPeriod === 'current_month') {
        start_date = new Date(today.getFullYear(), today.getMonth(), 1)
        end_date = today
      } else if (this.selectedPeriod === 'last_month') {
        start_date = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        end_date = new Date(today.getFullYear(), today.getMonth(), 0)
      } else if (this.selectedPeriod === 'last_3_months') {
        end_date = today
        start_date = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())
      } else if (this.selectedPeriod === 'last_6_months') {
        end_date = today
        start_date = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate())
      } else if (this.selectedPeriod === 'last_year') {
        end_date = today
        start_date = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
      } else if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
        start_date = new Date(this.customStartDate)
        end_date = new Date(this.customEndDate)
      } else {
        start_date = new Date(today.getFullYear(), today.getMonth(), 1)
        end_date = today
      }
      
      return {
        company_id: this.companyId,
        start_date: start_date.toISOString().split('T')[0],
        end_date: end_date.toISOString().split('T')[0]
      }
    },
    
    generateTabs() {
      const tabs = [{ id: 'summary', label: 'サマリー' }]
      
      this.mediaData.forEach(media => {
        tabs.push({
          id: media.media_type.toString(),
          label: media.media_name
        })
      })
      
      this.tabs = tabs
    },
    
    getMediaData(mediaType) {
      return this.mediaData.find(m => m.media_type.toString() === mediaType)
    },
    
    onPeriodChange() {
      if (this.selectedPeriod === 'custom') {
        if (this.customStartDate && this.customEndDate) {
          this.refreshData()
        }
      } else {
        this.refreshData()
      }
    }
  }
}
</script>

<style scoped>
.media-analysis-section {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
}

.analysis-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.period-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.period-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
}

.period-select:hover {
  border-color: #007bff;
}

.period-select:focus {
  outline: none;
  border-color: #007bff;
}

.custom-date-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.date-input:focus {
  outline: none;
  border-color: #007bff;
}

.refresh-btn {
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tab-navigation {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;
  overflow-x: auto;
}

.tab-button {
  padding: 10px 20px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
  white-space: nowrap;
}

.tab-button:hover {
  background-color: #e9ecef;
}

.tab-button.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.content-area {
  min-height: 400px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  text-align: center;
  padding: 48px;
}

.error-icon {
  font-size: 48px;
  color: #dc3545;
  margin-bottom: 16px;
}

.error-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
}

.retry-btn {
  padding: 10px 24px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-btn:hover {
  background-color: #0056b3;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .analysis-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .period-selector {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .custom-date-picker {
    width: 100%;
  }
  
  .date-input {
    flex: 1;
  }
  
  .refresh-btn {
    width: 100%;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/components/MediaAnalysisSection.vue